// Copyright (c) StrangeBee 2024

import React, { MouseEventHandler, ReactElement, useMemo } from 'react';
import { Button, Dropdown, Menu } from 'antd';
import { classList } from '@Helpers/className';
import { openMessage } from '@Helpers/notification';
import { useLocalMessage } from '@Helpers/locale';
import client from '@Client';
import { ActionOutput } from 'thehive-sdk';
import { ICONS } from '@Constants/iconsApp';

import './AnalyzerActions.scss';

type ParamsJobProps = { observableId: string; analyzerId: string; cortexId: string };

interface AnalyzerActionsProps {
    firstUsed?: boolean;
    disabled?: boolean;
    onClick?: MouseEventHandler<HTMLElement>;
    children?: ReactElement[];
}

type AnalyzerActionProps = {
    name: string;
    cortexIds: string[];
    observableId: string;
    firstUsed: boolean;
    loadingAnalysis?: ActionOutput;
};

const AnalyzerButton = ({
    onClick,
    firstUsed = false,
    children,
    disabled = false,
}: AnalyzerActionsProps): ReactElement => {
    const menu = <Menu>{children}</Menu>;

    const props = onClick ? { onClick } : {};

    const className = classList('analyzer_btn-reload', {
        'anticon-spin': !firstUsed && disabled,
        'analyzer_first-used': firstUsed,
    });

    const componentIcon = firstUsed ? <ICONS.FireOutlined /> : <ICONS.ReloadOutlined />;

    return children ? (
        <Dropdown overlay={menu} disabled={disabled} {...props}>
            <Button disabled={disabled} className={className} icon={componentIcon} />
        </Dropdown>
    ) : (
        <Button disabled={disabled} className={className} icon={componentIcon} onClick={onClick} />
    );
};

const AnalyzerActions = ({
    name,
    cortexIds,
    observableId,
    firstUsed,
    loadingAnalysis,
}: AnalyzerActionProps): ReactElement => {
    const i = useLocalMessage();

    const isMulti = cortexIds.length > 1;

    const paramsJobs: ParamsJobProps[] = useMemo(() => {
        return isMulti
            ? cortexIds.map((cortexId) => ({ observableId, analyzerId: name, cortexId }))
            : [{ observableId, analyzerId: name, cortexId: cortexIds[0] }];
    }, [cortexIds, isMulti, name, observableId]);

    const buildLauncher = (paramsOnClick: ParamsJobProps) => (): void => {
        openMessage('success', i('analyzer.launch.success'));
        client.AnalyzerController.executeJob(paramsOnClick).catch(() => {
            openMessage('error', i('analyzer.launch.error'));
        });
    };

    return isMulti ? (
        <AnalyzerButton firstUsed={firstUsed}>
            {paramsJobs.map((params, index) => (
                <Menu.Item key={index} onClick={buildLauncher(params)} icon={<ICONS.FireOutlined />}>
                    {params.cortexId}
                </Menu.Item>
            ))}
        </AnalyzerButton>
    ) : (
        <AnalyzerButton firstUsed={firstUsed} onClick={buildLauncher(paramsJobs[0])} disabled={!!loadingAnalysis} />
    );
};

export default AnalyzerActions;
